/**
 * Ativacao de notificacoes (spec 4.4 / 11).
 *
 * O fluxo tem quatro estados possiveis e nenhum deles e "erro", o que e o
 * motivo de existir um hook em vez de um botao com `try/catch`:
 *
 *   indisponivel -> o navegador nao suporta push (ou nao ha VAPID no ambiente)
 *   bloqueado    -> a pessoa negou a permissao; SO ela pode reverter, nas
 *                   configuracoes do navegador — nenhum codigo reabre o prompt
 *   desativado   -> suportado e permitido, sem inscricao ativa
 *   ativo        -> inscrito
 *
 * Tratar "bloqueado" como falha levaria a interface a oferecer um botao que
 * nunca funciona. Ele precisa explicar, nao tentar de novo.
 */

import { useCallback, useEffect, useState } from 'react';
import { uuidv7 } from '@cleartask/shared';
import { api } from '../../lib/api.js';

export type PushState = 'carregando' | 'indisponivel' | 'bloqueado' | 'desativado' | 'ativo';

/**
 * base64url -> ArrayBuffer.
 *
 * O `applicationServerKey` do `pushManager` nao aceita string: exige os 65
 * bytes crus da chave publica. Devolve o `ArrayBuffer` em vez da view porque
 * `Uint8Array` pode ser respaldada por `SharedArrayBuffer`, que a assinatura de
 * `BufferSource` recusa.
 */
function chaveParaBytes(base64Url: string): ArrayBuffer {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const binario = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));

  const buffer = new ArrayBuffer(binario.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
  return buffer;
}

function bytesParaBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';

  let binario = '';
  for (const byte of new Uint8Array(buffer)) binario += String.fromCharCode(byte);
  return btoa(binario).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function suportado(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export interface UsePushResult {
  state: PushState;
  /** Mensagem de erro da ultima tentativa, para a UI mostrar em vez de engolir. */
  erro: string | null;
  ativar: () => Promise<void>;
  desativar: () => Promise<void>;
  ocupado: boolean;
}

export function usePush(): UsePushResult {
  const [state, setState] = useState<PushState>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const sincronizarEstado = useCallback(async () => {
    if (!suportado()) {
      setState('indisponivel');
      return;
    }

    // Sem chave VAPID no ambiente nao ha o que ativar. Vale como
    // "indisponivel": e verdade sobre este servidor, nao sobre este navegador.
    const { publicKey } = await api.push.key().catch(() => ({ publicKey: null }));
    if (!publicKey) {
      setState('indisponivel');
      return;
    }

    if (Notification.permission === 'denied') {
      setState('bloqueado');
      return;
    }

    const registro = await navigator.serviceWorker.ready;
    const inscricao = await registro.pushManager.getSubscription();
    setState(inscricao ? 'ativo' : 'desativado');
  }, []);

  useEffect(() => {
    void sincronizarEstado();
  }, [sincronizarEstado]);

  const ativar = useCallback(async () => {
    setOcupado(true);
    setErro(null);

    try {
      const { publicKey } = await api.push.key();
      if (!publicKey) throw new Error('Este ambiente não tem push configurado.');

      const permissao = await Notification.requestPermission();
      if (permissao !== 'granted') {
        setState(permissao === 'denied' ? 'bloqueado' : 'desativado');
        return;
      }

      const registro = await navigator.serviceWorker.ready;

      // `userVisibleOnly` e obrigatorio nos navegadores baseados em Chromium: o
      // push silencioso nao e permitido na web, e o contrato e que toda
      // mensagem recebida vire notificacao visivel.
      const inscricao = await registro.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: chaveParaBytes(publicKey),
      });

      await api.push.subscribe({
        id: uuidv7(),
        endpoint: inscricao.endpoint,
        p256dh: bytesParaBase64Url(inscricao.getKey('p256dh')),
        auth: bytesParaBase64Url(inscricao.getKey('auth')),
        userAgent: navigator.userAgent.slice(0, 300),
      });

      setState('ativo');
    } catch (err) {
      // A inscricao local pode ter sido criada antes de o servidor falhar.
      // Deixa-la orfa faria o navegador se considerar inscrito e nada chegar —
      // o estado mais confuso possivel para quem tenta de novo.
      const registro = await navigator.serviceWorker.ready.catch(() => null);
      const inscricao = await registro?.pushManager.getSubscription();
      await inscricao?.unsubscribe().catch(() => undefined);

      setErro(err instanceof Error ? err.message : 'Não foi possível ativar as notificações.');
      setState('desativado');
    } finally {
      setOcupado(false);
    }
  }, []);

  const desativar = useCallback(async () => {
    setOcupado(true);
    setErro(null);

    try {
      const registro = await navigator.serviceWorker.ready;
      const inscricao = await registro.pushManager.getSubscription();
      if (!inscricao) {
        setState('desativado');
        return;
      }

      // O servidor primeiro: se o navegador cancelasse antes e a chamada
      // falhasse, a linha ficaria no banco apontando para um endpoint morto e
      // o cron gastaria uma requisicao por ciclo ate receber o primeiro 410.
      await api.push.unsubscribe(inscricao.endpoint).catch(() => undefined);
      await inscricao.unsubscribe();

      setState('desativado');
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Não foi possível desativar.');
    } finally {
      setOcupado(false);
    }
  }, []);

  return { state, erro, ativar, desativar, ocupado };
}
